<script setup lang="ts">
import { ref, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import usePrice from '@core/composables/use-price';
import type Product from '@client/monolith/legacy/types/product';
import type { ESProductHit } from '@client/modules/discovery/domain/catalog';
import { getImgCdnServiceInstance } from '@bc/shared/util';
import { CardProductProps } from '../types/card-product-props';
import { CardProductEmits } from '../types/card-product-emits';
import CardProduct from './card-product.vue';
import { ProductBuilder } from '../services/product-builder';
import { isTileSimplified } from '../services/tile-simplification';

const props = defineProps<
  {
    product: Product | ESProductHit;
    isExternalLink?: boolean;
  } & Omit<CardProductProps, 'product'>
>();
defineEmits<CardProductEmits>();

const { getConvertedPrice } = usePrice();
const i18n = useI18n();

const productViewModel = ref<Awaited<ReturnType<ProductBuilder['build']>>>();

watchEffect(async () => {
  productViewModel.value = await new ProductBuilder(getImgCdnServiceInstance(), i18n, getConvertedPrice)
    .with('id', props.product.id)
    .with('name', props.product.name as string)
    .with('images', props.product.images)
    .with('link', props.product.link)
    .with('variants', props.product.variants)
    .with('brandName', props.product.brand.name)
    .with('brandLink', props.product.brand.link)
    .with('discountPercentage', props.product.discount_rate)
    .with('simplified', isTileSimplified())
    .with('unitMultiplier', props.product.unit_multiplier)
    .with('tags', props.product.tags)
    .with('isNew', props.product.is_new)
    .with('wholeSalePrice', props.product.wholesale_price)
    .with('originalWholeSalePrice', props.product.original_wholesale_price)
    .with('retailPrice', props.product.retail_price)
    .with('available_at', props.product.available_at)
    .with('brand', props.product.brand)
    .build();
});
</script>

<template>
  <CardProduct
    :product="productViewModel"
    :qtac-product="product"
    :page-number="pageNumber"
    :position="position"
    :can-get-wholesale-price="canGetWholesalePrice"
    :is-user-authenticated="isUserAuthenticated"
    :is-external-link="isExternalLink"
    :with-drawer="withDrawer"
    :hide-volume-discount="hideVolumeDiscount"
    :hide-estimated-delivery="hideEstimatedDelivery"
    @clicked-drawer="$emit('clickedDrawer', $event)"
    @clicked="$emit('clicked', $event)"
    @brand-clicked="$emit('brandClicked', $event)"
    @add-to-cart-clicked="$emit('addToCartClicked', $event)"
    @added-to-cart="$emit('addedToCart', $event)"
    @like-clicked="$emit('likeClicked', $event)"
    @unlike-clicked="$emit('unlikeClicked', $event)"
  />
</template>
